import { Box, Flex, HStack, Text, Avatar, rem } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

import { Credit } from '../../types';

interface DisplayOnlyLink {
  isDisplayOnly: true;
  href?: string;
}

interface Props extends React.HTMLProps<HTMLAnchorElement> {
  isDisplayOnly: false;
}

type EnhancedNextLinkProps = DisplayOnlyLink | Props;

const EnhancedNextLink = (props: React.PropsWithChildren<EnhancedNextLinkProps>) => {
  if (props.isDisplayOnly) {
    return <>{props.children}</>;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, onClick, isDisplayOnly: _, ...rest } = props;

  return (
    <a data-nav-type="n" onClick={onClick} {...rest}>
      {children}
    </a>
  );
};

export function Credits({ credits, onClick }: { credits: Credit[]; onClick?: () => void }) {
  return (
    <>
      <Text as="h2" variant="heading-sm">
        {translate('Credits')}
      </Text>
      <Box as="nav" aria-label="Video Credits" data-testid="credits-nav">
        <HStack as="ul" wrap="wrap" spacing={200} maxW={rem(1024)}>
          {credits.map((credit) => (
            <Box as="li" key={credit.uri} flexBasis={{ base: '100%', md: rem(240) }}>
              <EnhancedNextLink
                isDisplayOnly={!Boolean(credit.userLink)}
                href={credit.userLink}
                onClick={onClick}
              >
                <HStack>
                  {/* Avatar needs to be wrapped, cause it doesn't directly take customization props; it needs to have shrink 0 style to avoid shrinking when following text is too long */}
                  <Box minW="sm" flexShrink={0}>
                    <Avatar
                      src={credit.avaSrc}
                      srcSet={credit.avaSrcSet}
                      size="sm"
                      alt={credit.name}
                      nameProps={{ name: credit.name }}
                    />
                  </Box>
                  <Flex direction="column" justifyContent="center">
                    <Text
                      title={credit.name}
                      variant="heading-xs"
                      wordBreak="break-word"
                      noOfLines={2}
                    >
                      {credit.name}
                    </Text>
                    {credit.role ? (
                      <Text
                        title={credit.role}
                        variant="body-md"
                        color="text-secondary"
                        wordBreak="break-all"
                        noOfLines={1}
                        data-testid="credit-role"
                      >
                        {credit.role}
                      </Text>
                    ) : null}
                  </Flex>
                </HStack>
              </EnhancedNextLink>
            </Box>
          ))}
        </HStack>
      </Box>
    </>
  );
}
